import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { SelectSourceOptionType } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import DataManagerConnectedTableRowRequestedComponent from 'webapp/components/data-manager/connected/table-row/requested';
import DataManagerConnectedTableRowSelectedComponent from 'webapp/components/data-manager/connected/table-row/selected';
import DataManagerConnectedTableRowSourceComponent from 'webapp/components/data-manager/connected/table-row/source';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import TableSorterComponent from 'webapp/components/table-sorter';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';

import type ConnectRequest from 'webapp/models-custom/connect-request';
import type ConnectSelection from 'webapp/models-custom/connect-selection';
import FilterSortSource from 'webapp/models-custom/filter-sort/source';
import type Source from 'webapp/models-custom/source';

interface Args {
	onAdd: () => void;
}

export default class DataManagerConnectedTableComponent extends Component<Args> {
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('data-manager') declare dataManagerService: Service.DataManager;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('navigation') declare navigationService: Service.Navigation;

	filterSort: FilterSortSource;
	visibleCheckContainerNode: HTMLElement | null = null;
	addButtonText = 'Add data source';

	datasetsCountFor = (source: Source): number => {
		return (source.id && this.datasetsBySourceId.get(source.id)?.length) || 0;
	};

	isExpandedByDefaultFor = (source: Source): boolean => {
		return (
			this.filterSort.isErrorFilterActive ||
			(this.filterSort.isSearchActive && this.sourceIdsWithMatchingDatasets.has(source.id!))
		);
	};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortSource({
			getAllItems: () => {
				return this.allItems;
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}
			},
		});
	}

	private processSource(
		source: Source,
		sourceMap: Map<string, Source>,
		rootSources: Source[],
		isErrorFilter: boolean,
	): void {
		const mappedSource = sourceMap.get(source.id!);
		if (!mappedSource) return;

		const shouldAddToRootSources = isErrorFilter
			? mappedSource.statusObject.isError ||
				mappedSource.childSources.some((s) => s.statusObject.isError)
			: true;
		const shouldAddSourceToParent = isErrorFilter ? mappedSource.statusObject.isError : true;

		const parentId = source.parentInfo?.spaceAccessId?.toString();
		const parent = parentId ? sourceMap.get(parentId) : null;

		if (!parent) {
			if (shouldAddToRootSources) {
				rootSources.push(mappedSource);
			}
			return;
		}

		const isSourceAlreadyChild = parent.childSources.some((s) => s.id === mappedSource.id);
		if (!isSourceAlreadyChild && shouldAddSourceToParent) {
			parent.childSources.push(mappedSource);
		}
	}

	get allItems() {
		// a delete the user confirmed is hidden, with its datasets, unless it fails; filtering here
		// keeps it out of the results, the empty state and the counts too
		const filteredSources = this.mmSourcesService.sourceModels.filter((source) => {
			return (
				source.selectedSourceOption !== SelectSourceOptionType.MERGED &&
				!this.dataManagerService.isPendingDeletion(source)
			);
		});

		return [
			...filteredSources,
			...this.appNotificationsService.connectRequests,
			...this.appNotificationsService.connectSelections,
		];
	}

	@cached
	get datasetsBySourceId(): Map<string, Source[]> {
		const datasetsBySourceId = new Map<string, Source[]>();

		this.mmSourcesService.sourceModels.forEach((source) => {
			const parentId = source.parentInfo?.spaceAccessId?.toString();

			// a delete the user confirmed leaves the count and the search expansion
			if (
				parentId &&
				!source.isMergedDataset &&
				!this.dataManagerService.isPendingDeletion(source)
			) {
				const datasets = datasetsBySourceId.get(parentId) || [];
				datasets.push(source);
				datasetsBySourceId.set(parentId, datasets);
			}
		});

		return datasetsBySourceId;
	}

	@cached
	get sourcesById(): Map<string, Source> {
		return new Map(this.mmSourcesService.sourceModels.map((source) => [source.id!, source]));
	}

	get shouldAutoExpandDatasets(): boolean {
		return this.filterSort.isSearchActive || this.filterSort.isErrorFilterActive;
	}

	@cached
	get sourceIdsWithMatchingDatasets(): Set<string> {
		const ids = new Set<string>();
		(this.filterSort.results || []).forEach((result) => {
			const parentId = (result as Source).parentInfo?.spaceAccessId?.toString();
			if (parentId) {
				ids.add(parentId);
			}
		});
		return ids;
	}

	get sortedSourcesList() {
		const allSources: Source[] = [];
		const requestedSources: ConnectRequest[] = [],
			selectedSources: ConnectSelection[] = [],
			connectedSources: Source[] = [];

		const isErrorFilter = this.filterSort.isErrorFilterActive;
		const shouldAutoExpandDatasets = this.shouldAutoExpandDatasets;

		// Establish parent child relationships for connected sources
		const sourceMap = new Map<string, Source>();

		(this.filterSort.results || []).forEach((result) => {
			if ((result as ConnectRequest).isConnectRequest) {
				requestedSources.push(result as ConnectRequest);
			} else if ((result as ConnectSelection).isConnectSelection) {
				selectedSources.push(result as ConnectSelection);
			} else {
				const source = result as Source;

				allSources.push(source);

				// Reset childSources to build clean tree
				source.childSources = [];
				sourceMap.set(source.id!, source);
			}
		});

		// A dataset that matches without its data source is shown under that data source
		const sourcesWithParents: Source[] = [];
		allSources.forEach((source) => {
			const parentId = source.parentInfo?.spaceAccessId?.toString();
			const parent =
				parentId && shouldAutoExpandDatasets && !sourceMap.has(parentId)
					? this.sourcesById.get(parentId)
					: undefined;

			if (parent) {
				parent.childSources = [];
				sourceMap.set(parentId!, parent);
				sourcesWithParents.push(parent);
			}
			sourcesWithParents.push(source);
		});

		// A data source that matches the search brings all its datasets along
		if (this.filterSort.isSearchActive) {
			const filteredItems = new Set(this.filterSort.filteredItems);
			allSources.forEach((source) => {
				if (source.parentInfo?.spaceAccessId) return;

				this.datasetsBySourceId.get(source.id!)?.forEach((dataset) => {
					if (!sourceMap.has(dataset.id!) && filteredItems.has(dataset)) {
						dataset.childSources = [];
						sourceMap.set(dataset.id!, dataset);
						sourcesWithParents.push(dataset);
					}
				});
			});
		}

		// Attach datasets first, so each data source knows its datasets when it's placed
		const isAttachable = (source: Source) => {
			const parentId = source.parentInfo?.spaceAccessId?.toString();
			return !!parentId && sourceMap.has(parentId);
		};
		[
			...sourcesWithParents.filter(isAttachable),
			...sourcesWithParents.filter((source) => !isAttachable(source)),
		].forEach((source) =>
			this.processSource(source, sourceMap, connectedSources, isErrorFilter),
		);

		return [...selectedSources, ...requestedSources, ...connectedSources];
	}

	get addButtonContainerNode() {
		return isAlive(this) ? this.navigationService.addButtonContainerNode : null;
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		this.eventManagerService.trigger(AppEvent.DATAMANAGER_SOURCES_CONTENT_CHANGED);
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;
	}
	<template>
		<div class="bottom-padding">

			<FilterLineProxyComponent @model={{this.filterSort}} />

			<FilterNoResultsComponent @model={{this.filterSort}} />

			<table
				id="sources-list"
				class="flexible-table bigtable has-avatar {{if this.filterSort.noResults 'hide'}}"
				{{didInsert this.setupView}}
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange this.filterSort.results}}
			>
				<colgroup>
					<col class="h0" />
					<col width="20%" />
					<col />
					<col />
					<col />
					<col width="15%" />
					<col class="button-ct" />
				</colgroup>
				<thead>
					<tr>
						<th class="h0">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="title"
								@name="Title"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th></th>
						<th class="prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="created"
								@name="Created"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="prio-low">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="userObjsCount"
								@name="Permissions"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="prio-medium">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="statusObjectLevel"
								@name="Sync frequency"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="prio-medium">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="syncStatus"
								@name="Status"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="prio-medium">
							<TableSorterComponent
								@selectedProp={{this.filterSort.sortProp}}
								@selectedOrder={{this.filterSort.sortOrder}}
								@prop="countBasicMetrics"
								@name="Metrics"
								@changeProp={{this.filterSort.changeSortProp}}
								@changeOrder={{this.filterSort.changeSortOrder}}
							/>
						</th>
						<th class="button-ct"></th>
					</tr>
				</thead>
				<tbody>
					{{#each this.sortedSourcesList as |source|}}
						{{#if source.isConnectRequest}}
							<DataManagerConnectedTableRowRequestedComponent @model={{source}} />
						{{else if source.isConnectSelection}}
							<DataManagerConnectedTableRowSelectedComponent @model={{source}} />
						{{else}}
							<DataManagerConnectedTableRowSourceComponent
								@model={{source}}
								@datasetsCount={{this.datasetsCountFor source}}
								@shouldAutoExpandDatasets={{this.shouldAutoExpandDatasets}}
								@isExpandedByDefault={{this.isExpandedByDefaultFor source}}
							/>
						{{/if}}
					{{/each}}
				</tbody>
			</table>

			<TableAddNewItemComponent
				@title={{this.addButtonText}}
				@addNewItem={{@onAdd}}
				@model={{this.filterSort}}
			/>
		</div>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent @text={{this.addButtonText}} @callTrigger={{@onAdd}} />
			{{/in-element}}
		{{/if}}
	</template>
}
